import { HightouchRegions } from "src/utils/regions";

const allowedOrigins = new Set(
  Object.values(HightouchRegions)
    .filter((r) => r.apiEndpoint)
    .map((r) => new URL(r.apiEndpoint).origin),
);

export function isHightouchApiUrl(url: URL): boolean {
  if (url.protocol !== "https:" && url.protocol !== "http:") return false;
  if (allowedOrigins.has(url.origin)) return true;
  const apiBase = import.meta.env.VITE_API_BASE_URL;
  return !!apiBase && url.origin === new URL(apiBase).origin;
}

// Resolved on first use, not at module scope: `new URL(undefined)` throws, so
// evaluating this eagerly makes merely *importing* this module fatal anywhere
// VITE_API_BASE_URL isn't set — which takes down any test that happens to pull
// it in transitively, long before a URL needs resolving.
let cachedApiPathPrefix: string | undefined;
const getApiPathPrefix = (): string => {
  if (cachedApiPathPrefix === undefined) {
    cachedApiPathPrefix = new URL(import.meta.env.VITE_API_BASE_URL).pathname;
  }
  return cachedApiPathPrefix;
};

/**
 * Resolves a stored agent file URL for use in the current app.
 *
 * Preserves the regional API hostname from the stored URL so the request
 * routes to the correct regional CloudFront distribution, but swaps the
 * path prefix for the one in VITE_API_BASE_URL.
 *
 * Example (apiPathPrefix = "/api"):
 *   stored:  https://api.azure-eastus.hightouch.com/api/agents/files?key=...
 *   result:  https://api.azure-eastus.hightouch.com/api/agents/files?key=...
 *   → azure-eastus API → azure-eastus CloudFront → 200
 */
export function resolveAgentFileUrl(storedUrl: string): string {
  const match = storedUrl.match(/\/agents\/.*/);
  if (!match) return storedUrl;

  const storedOrigin = new URL(storedUrl).origin;
  if (allowedOrigins.has(storedOrigin)) {
    return `${storedOrigin}${getApiPathPrefix()}${match[0]}`;
  }

  return `${import.meta.env.VITE_API_BASE_URL}${match[0]}`;
}

export function resolveAgentFileDownloadUrl(
  storedUrl: string,
  downloadFilename: string,
): string {
  const url = new URL(resolveAgentFileUrl(storedUrl));
  url.searchParams.set("download", "true");
  url.searchParams.set("downloadFilename", downloadFilename);
  return url.toString();
}
